import { useEffect, useRef } from "react";
import { CourseDataApi } from "./api/course-data.api";
import { DeviceDataPayload } from "./entities/features-api";
import { AnalyticsService } from "./analytics";
import { getGA4ClientId, getFacebookCookies } from "@features/helpers";

export function useTrackDeviceData() {
    const hasTracked = useRef(false);

    useEffect(() => {

        if (hasTracked.current) return;
        if (typeof window === "undefined") return;

        hasTracked.current = true;

        const waitForAnalyticsCookies = async (maxAttempts: number = 15, intervalMs: number = 500): Promise<{ ga: string; fb: { _fbp: string; _fbc: string } }> => {
            return new Promise((resolve) => {
                let attempts = 0;

                const checkCookies = () => {
                    const gaClientId = getGA4ClientId();
                    const fbCookies = getFacebookCookies();

                    const hasGa = !!gaClientId;
                    const hasFb = !!(fbCookies._fbp || fbCookies._fbc);

                    if (hasGa && hasFb) {
                        resolve({ ga: gaClientId, fb: fbCookies });
                        return;
                    }

                    attempts++;

                    if (attempts >= maxAttempts) {
                        resolve({ ga: gaClientId, fb: fbCookies });
                        return;
                    }

                    setTimeout(checkCookies, intervalMs);
                };

                checkCookies();
            });
        };

        const trackDevice = async () => {
            try {

                await Promise.race([
                    AnalyticsService.deviceIdReady,
                    new Promise((resolve) => setTimeout(resolve, 3000)),
                ]);

                const getDeviceId = (): string | null => {
                    try {
                        return localStorage.getItem("user_uuid");
                    } catch {
                        return null;
                    }
                };

                const deviceId = getDeviceId();
                if (!deviceId) {
                    if (process.env.NODE_ENV === "development") {
                        console.log("[Device Analytics] Device ID not found, skipping tracking");
                    }
                    return;
                }

                const urlParams = new URLSearchParams(window.location.search);

                const { ga: gaFromCookie, fb: fbCookies } = await waitForAnalyticsCookies();

                const payload: DeviceDataPayload = {
                    device_id: deviceId,
                    utm_source: urlParams.get("utm_source") || undefined,
                    utm_medium: urlParams.get("utm_medium") || undefined,
                    utm_campaign: urlParams.get("utm_campaign") || undefined,
                    utm_content: urlParams.get("utm_content") || undefined,
                    utm_term: urlParams.get("utm_term") || undefined,
                    onboarding_id: urlParams.get("onboarding_id") || undefined,
                    user_agent: navigator.userAgent,
                    ga: gaFromCookie,
                    gclid: urlParams.get("gclid") || undefined,
                    fb_pixel_id: fbCookies._fbp,
                    fb_click_id: fbCookies._fbc,
                    adset_id: urlParams.get("adset_id") || undefined,
                    ad_id: urlParams.get("ad_id") || undefined,
                    ad_name: urlParams.get("ad_name") || undefined,
                    query: urlParams.toString() || undefined,
                };

                const cleanPayload = Object.fromEntries(
                    Object.entries(payload).filter(([_, v]) => v !== undefined)
                ) as DeviceDataPayload;

                await CourseDataApi.trackDeviceData(cleanPayload);

                AnalyticsService.trackEvent("page_viewed", { visit_url: window.location.href });

                if (process.env.NODE_ENV === "development") {
                    console.log("[Device Analytics] Successfully tracked device data", cleanPayload);
                }
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    console.error("[Device Analytics] Failed to track device data:", error);
                }
            }
        };

        trackDevice();
    }, []);
}
